// /welcome: signed in but not yet activated (staged rollout).
import React, { useState } from 'react';
import axios from 'axios';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button } from '../ui';
import { useAuth } from '../../context/useAuth.jsx';
import { AuthShell, FormMsg } from './AuthShell.jsx';

const INTENTS = [['creating', 'Creating'], ['using', 'Using'], ['both', 'Both']];

export default function Welcome() {
  const { isAuthenticated, isCheckingAuth, activated, user, signOut, signOutEverywhere } = useAuth();
  const navigate = useNavigate();
  const [chosen, setChosen] = useState('');
  const [saved, setSaved] = useState(false);
  const [msg, setMsg] = useState({ text: '', error: false });

  if (isCheckingAuth) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (activated) return <Navigate to="/app" replace />;

  const save = async () => {
    if (!chosen) return;
    setMsg({ text: '', error: false });
    try {
      // The axios Bearer default (in-memory access token) is attached.
      await axios.post('/auth/profile/intent', { intent: chosen }, { withCredentials: true });
      setSaved(true);
      setMsg({ text: 'Saved. Thank you!', error: false });
    } catch (err) {
      setMsg({ text: "Couldn't save that. Please try again.", error: true });
    }
  };

  const out = (fn) => async () => { await fn(); navigate('/', { replace: true }); };

  return (
    <AuthShell>
      <h1 className="text-2xl font-semibold text-gray-900">You're in. Welcome!</h1>
      <p className="text-sm text-gray-600 mt-2">
        Your account is ready. We're opening Flowize in stages, and we'll email{' '}
        <b className="text-gray-900">{user?.email || 'your address'}</b> as soon as yours is switched on.
      </p>
      <div className="mt-6 p-4 rounded-lg border border-gray-200">
        <h3 className="font-medium text-gray-900">What brings you here?</h3>
        <p className="text-sm text-gray-500 mb-3">Optional. It helps us know who to switch on first.</p>
        <div className="flex gap-2" role="group" aria-label="What brings you here?">
          {INTENTS.map(([value, label]) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={chosen === value ? 'primary' : 'secondary'}
              aria-pressed={chosen === value}
              onClick={() => { setChosen(value); setSaved(false); setMsg({ text: '', error: false }); }}
            >
              {label}
            </Button>
          ))}
        </div>
        <Button type="button" variant="primary" size="sm" className="mt-3" onClick={save} disabled={!chosen || saved}>Save</Button>
        <FormMsg error={msg.error}>{msg.text}</FormMsg>
      </div>
      <p className="mt-6 text-xs font-semibold uppercase tracking-wide text-gray-500">Coming to your account</p>
      <ul className="text-sm text-gray-700 mt-2 divide-y divide-gray-100">
        {['Buy credits', 'Connect your AI assistant', 'Publish a skill'].map((t) => (
          <li key={t} className="flex justify-between py-2">{t}<span className="text-xs text-gray-400">Soon</span></li>
        ))}
      </ul>
      <div className="flex gap-4 mt-6 text-sm">
        <button type="button" className="underline" onClick={out(signOut)}>Sign out</button>
        <button type="button" className="underline text-gray-500" onClick={out(signOutEverywhere)}>Sign out everywhere</button>
      </div>
    </AuthShell>
  );
}
